/* eslint-disable */
import type { LocalizableString } from "../cash/localization/localization.pb";
import type { ClientScenario } from "./api.pb";
import type { AppMessagePayload } from "./app_messaging.pb";
import type { BlockerAction } from "./blocker_action.pb";

/** Represents the status result for a payment flow or linking action in the Cash 2.0 app. */
export interface StatusResult {
  icon?: StatusResultIcon;
  /** The main text shown to the customer that describes the status. */
  text?: string;
  localizable_text?: LocalizableString;
  primary_button?: StatusResultButton;
  secondary_button?: StatusResultButton;
  /** The promo text shown directly above the buttons. */
  promo_text?: string;
  localizable_promo_text?: LocalizableString;
  /** The app message promotion, shown directly above the buttons. */
  promo_payload?: AppMessagePayload;
  /** Describes the action to be taken by the application. */
  action?: StatusResultAction;
  /** Defines the location where the app should redirect the customer. */
  redirect_uri?: string;
  /** It's a joyous occasion. Release some confetti to celebrate. */
  show_confetti?: boolean;
  /** Defines the support action to take on START_SUPPORT_FLOW */
  support_action?: StatusResultSupportAction;
}

export enum StatusResultAction {
  /** SHOW_DIALOG - Show a dialog box with buttons. */
  SHOW_DIALOG = "SHOW_DIALOG",
  /** INSTANT_REDIRECT - Instantly redirect the app to an external URI defined by redirect_uri. */
  INSTANT_REDIRECT = "INSTANT_REDIRECT",
  /** START_SUPPORT_FLOW - Start a support flow without first presenting the customer with a blocker * */
  START_SUPPORT_FLOW = "START_SUPPORT_FLOW",
  /** INSTANT_INTERNAL_REDIRECT - Instantly redirect the app to client route defined by redirect_uri. */
  INSTANT_INTERNAL_REDIRECT = "INSTANT_INTERNAL_REDIRECT",
  /** INSTANT_BACK_NAVIGATION - Instantly return the user to their previously active application or open a fallback url if redirect_uri is defined. Introduced in 3.77 */
  INSTANT_BACK_NAVIGATION = "INSTANT_BACK_NAVIGATION",
}

export enum StatusResultIcon {
  /** SUCCESS - Mapped to a check mark. Indicates cash or request was successfully sent. */
  SUCCESS = "SUCCESS",
  /**
   * INSTANT - Mapped to a lightning bolt. Indicates cash was deposited instantly.
   * Supported in 2.12 and above.
   */
  INSTANT = "INSTANT",
  /**
   * BANK - Mapped to an X for now, eventually should be a bank image. Indicates that the sender's bank
   * declined the payment.
   */
  BANK = "BANK",
  /** FAILURE - Mapped to an X. Indicates that the payment failed (for any reason other than a bank decline). */
  FAILURE = "FAILURE",
  /**
   * ACTION_REQUIRED - Mapped to an exclamation mark. Indicates that there is action required for the current
   * customer.
   */
  ACTION_REQUIRED = "ACTION_REQUIRED",
  /**
   * PENDING - Mapped to an ellipsis. Indicates that no further action is required of the current customer,
   * but the requested action may not complete immediately.
   * Supported in v2.17 and later clients.
   */
  PENDING = "PENDING",
  /**
   * VERIFICATION_REQUIRED - Show verification shield. Indicates that the flow failed and the customer must complete
   * enhanced verification to proceed.
   * Supported in v2.41 and later clients.
   */
  VERIFICATION_REQUIRED = "VERIFICATION_REQUIRED",
  /** PACKAGE - Package icon. Used for showing that a card is shipped. */
  PACKAGE = "PACKAGE",
  /**
   * ACCOUNT_LOCKED - Show a lock icon. Indicates that the customer's account is temporarily locked.
   * Supported in v3.8 and later clients.
   */
  ACCOUNT_LOCKED = "ACCOUNT_LOCKED",
  /** NONE - Don't show an icon. */
  NONE = "NONE",
}

/** When START_SUPPORT_FLOW is used, specify either a support node or entity id for context */
export interface StatusResultSupportAction {
  support_flow_node?: string;
  entity_id?: string;
}

export interface StatusResultButton {
  action?: StatusResultButtonButtonAction;
  /** The text to display in the button. */
  text?: string;
  localizable_text?: LocalizableString;
  /**
   * The URL to navigate to when the button is clicked.
   * Must be populated when button_action is OPEN_URL.
   *
   * @deprecated
   */
  url?: string;
  /**
   * The client scenario to be completed.
   * Must be populated when button_action is COMPLETE_CLIENT_SCENARIO.
   *
   * @deprecated
   */
  client_scenario?: ClientScenario;
  /**
   * The node to begin at in the support flow.
   * Optional. May be populated when button_action is START_SUPPORT_FLOW.
   *
   * @deprecated
   */
  support_flow_node?: string;
  /**
   * Action to perform.
   * Must be populated when button_action is BLOCKER_ACTION.
   */
  blocker_action?: BlockerAction;
}

export enum StatusResultButtonButtonAction {
  /** PAY_SCREEN - Dismiss the status result. */
  PAY_SCREEN = "PAY_SCREEN",
  /** LINK_CARD - Navigate to the link card screen. */
  LINK_CARD = "LINK_CARD",
  /**
   * OPEN_URL - Open the URL in the url field.
   *
   * @deprecated
   */
  OPEN_URL = "OPEN_URL",
  /** VERIFY_IDENTITY - Begin an identity verification flow. */
  VERIFY_IDENTITY = "VERIFY_IDENTITY",
  /** INVITATION_SCREEN - Links to the invitation screen. Added for v2.14. */
  INVITATION_SCREEN = "INVITATION_SCREEN",
  /** ADD_CASH - Begin an Add Cash flow. */
  ADD_CASH = "ADD_CASH",
  /**
   * CARD_DRAWER - Navigate to the card drawer.
   *
   * @deprecated
   */
  CARD_DRAWER = "CARD_DRAWER",
  /**
   * COMPLETE_CLIENT_SCENARIO - Complete the provided client scenario.
   *
   * @deprecated
   */
  COMPLETE_CLIENT_SCENARIO = "COMPLETE_CLIENT_SCENARIO",
  /**
   * START_SUPPORT_FLOW - Start a support flow at the provided node.
   *
   * @deprecated
   */
  START_SUPPORT_FLOW = "START_SUPPORT_FLOW",
  /**
   * PAY_WITH_CASH_REDIRECT - Action to redirect to the platform configured external URL. Used for PAY_WITH_CASH.
   *
   * @deprecated
   */
  PAY_WITH_CASH_REDIRECT = "PAY_WITH_CASH_REDIRECT",
  /** BLOCKER_ACTION - Perform action specified by blocker_action. */
  BLOCKER_ACTION = "BLOCKER_ACTION",
}
